import type { LucideIcon } from "lucide-react";
import type { ReactNode } from "react";
import { cn } from "../../lib/utils";
import { DomainCard } from "./DomainCard";
import { domainClasses, type Domain } from "./domains";
import { IconTile } from "./IconTile";

export interface LayerCardProps {
  n: number;
  domain: Domain;
  icon: LucideIcon;
  /** e.g. "Lager 2". */
  label: ReactNode;
  title: ReactNode;
  rule: ReactNode;
  /** How often this layer changes and who changes it, e.g. "Ungefär en gång per år". */
  cadence?: { label: ReactNode; text: ReactNode };
  /** The same example visits, shown in this layer. */
  examples?: { title: ReactNode; detail: ReactNode }[];
}

/** One layer of the four-layer content model (Beviljat → Utfört). */
export function LayerCard({
  domain,
  icon,
  label,
  title,
  rule,
  cadence,
  examples,
}: LayerCardProps) {
  const d = domainClasses[domain];
  return (
    <DomainCard domain={domain} className="h-full gap-3 p-5">
      <div className="flex items-center gap-2.5">
        <IconTile icon={icon} domain={domain} size="xs" />
        <span
          className={cn(
            "font-mono text-[11px] font-semibold uppercase tracking-[0.08em]",
            d.ink,
          )}
        >
          {label}
        </span>
      </div>
      <h3
        className={cn(
          "hyphens-auto break-words text-[19px] font-extrabold",
          d.ink,
        )}
      >
        {title}
      </h3>
      <p className="text-sm leading-normal text-caire-ink/85">{rule}</p>
      {cadence ? (
        <div className="flex flex-col gap-1 border-t border-caire-ink/10 pt-3">
          <span
            className={cn(
              "font-mono text-[11px] font-semibold uppercase tracking-[0.08em]",
              d.ink,
            )}
          >
            {cadence.label}
          </span>
          <p className="text-sm leading-normal text-caire-ink/85">
            {cadence.text}
          </p>
        </div>
      ) : null}
      {examples?.length ? (
        <ul className="mt-auto flex flex-col gap-2">
          {examples.map((example, i) => (
            <li
              key={i}
              className="flex flex-col gap-0.5 rounded-xl bg-caire-surface px-3 py-2.5"
            >
              <span className="text-[13px] font-bold text-caire-ink">
                {example.title}
              </span>
              <span className={cn("font-mono text-xs font-medium", d.ink)}>
                {example.detail}
              </span>
            </li>
          ))}
        </ul>
      ) : null}
    </DomainCard>
  );
}
